import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountCircle,
  CreditCard,
  Subscriptions,
  Timeline,
} from '@mui/icons-material';

interface ProfileLayoutProps {
  children: React.ReactNode;
}

const ProfileLayout: React.FC<ProfileLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentTab = location.pathname.split('/').pop() || 'activity';
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    navigate(`/profile/${newValue}`);
  };

  const tabs = [
    {
      value: 'activity',
      label: 'Activity',
      icon: <Timeline />,
    },
    {
      value: 'account',
      label: 'Account',
      icon: <AccountCircle />,
    },
    {
      value: 'subscription',
      label: 'Subscription',
      icon: <Subscriptions />,
    },
    {
      value: 'payment',
      label: 'Payment',
      icon: <CreditCard />,
      badge: 'Coming Soon',
      disabled: true,
    },
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            Profile Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review your activity and manage your account and subscription
          </Typography>
        </Box>

        {/* Navigation */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant={isMdUp ? 'fullWidth' : 'scrollable'}
            scrollButtons="auto"
            allowScrollButtonsMobile
            indicatorColor="primary"
            textColor="primary"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                icon={tab.icon}
                iconPosition="start"
                disabled={tab.disabled}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="medium">
                      {tab.label}
                    </Typography>
                    {tab.badge && (
                      <Chip
                        label={tab.badge}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Box>
                }
                sx={{
                  textTransform: 'none',
                  minHeight: 48,
                }}
              />
            ))}
          </Tabs>
        </Paper>

        {/* Content */}
        <Box>{children}</Box>
      </Box>
    </Container>
  );
};

export default ProfileLayout;
